import React, { useEffect, useMemo, useState, useCallback } from "react";
import Sidebar from "../components/Sidebar";
import { Bars3Icon, EyeIcon, PlusIcon, PencilIcon, InformationCircleIcon } from "@heroicons/react/24/outline";
import axios from "axios";
import { SERVER_URL } from "../config";
import TableHeaderFilter from "../components/TableHeaderfilter";
import { FaServer, FaUserPlus } from "react-icons/fa";

import ServerFormModal from "../components/serverManagement/ServerFormModal";
import UserFormModal from "../components/serverManagement/UserFormModal";
import UsersListModal from "../components/serverManagement/UsersListModal";
import DetailsModal from "../components/serverManagement/DetailsModal";
import UserHistoryModal from "../components/serverManagement/UserHistoryModal";
import RenewalReportModal from "../components/serverManagement/RenewalReportModal";
import UserGroupModal from "../components/serverManagement/UserGroupModal";
import DashboardCharts from "../components/serverManagement/DashboardCharts";
import AdminLogsModal from "../components/serverManagement/AdminLogsModal";
import { Toast, ReasonDialog } from "../components/serverManagement/Shared";
import {
    postForm,
    getActionTakenBy,
    isAdminUser,
    normalizeStatus,
    formatMoney,
    EMPTY_SERVER_FORM,
    EMPTY_USER_FORM,
} from "../components/serverManagement/utils";

const ServerManagement = () => {
    // Check if user is admin based on privilages = 2
    const isAdmin = useMemo(() => {
        try {
            // Try to get from 'employee' key first, fallback to 'user'
            let userData = localStorage.getItem('employee');
            if (!userData) {
                userData = localStorage.getItem('user');
            }
            const user = JSON.parse(userData || '{}');
            return user.privilages === 2;
        } catch {
            return false;
        }
    }, []);

    // Get employee_id from localStorage
    const getEmployeeId = useCallback(() => {
        try {
            // Try to get from 'employee' key first, fallback to 'user'
            let userData = localStorage.getItem('employee');
            if (!userData) {
                userData = localStorage.getItem('user');
            }
            const user = JSON.parse(userData || '{}');
            return user.employee_id || '';
        } catch {
            return '';
        }
    }, []);

    // Get employee_name for action tracking
    const getEmployeeName = useCallback(() => {
        try {
            let userData = localStorage.getItem('employee');
            if (!userData) {
                userData = localStorage.getItem('user');
            }
            const user = JSON.parse(userData || '{}');
            return user.employee_name || user.username || '';
        } catch {
            return '';
        }
    }, []);

    const [search, setSearch] = useState("");
    const [rawRows, setRawRows] = useState([]);
    const [userData, setUserData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [loadError, setLoadError] = useState("");
    const [sidebarOpen, setSidebarOpen] = useState(false);

    const [showServerModal, setShowServerModal] = useState(false);
    const [showUserModal, setShowUserModal] = useState(false);
    const [showUserDetailsModal, setShowUserDetailsModal] = useState(false);
    const [showDetailsModal, setShowDetailsModal] = useState(false);
    const [showRenewalModal, setShowRenewalModal] = useState(false);
    const [showLogsModal, setShowLogsModal] = useState(false);
    const [historySubject, setHistorySubject] = useState(null);
    const [groupModal, setGroupModal] = useState(null);

    const [selectedServer, setSelectedServer] = useState(null);
    const [detailsSubject, setDetailsSubject] = useState(null);
    const [editingServer, setEditingServer] = useState(null);
    const [editingUser, setEditingUser] = useState(null);

    const [serverForm, setServerForm] = useState(EMPTY_SERVER_FORM);
    const [userForm, setUserForm] = useState(EMPTY_USER_FORM);

    const [savingServer, setSavingServer] = useState(false);
    const [savingUser, setSavingUser] = useState(false);
    const [formError, setFormError] = useState("");

    const [toast, setToast] = useState(null);
    const notify = (message, type = "success") => setToast({ message, type });

    const [reasonDialog, setReasonDialog] = useState(null);

    const [serverNameFilter, setServerNameFilter] = useState([]);
    const [serverPortFilter, setServerPortFilter] = useState([]);
    const [providerFilter, setProviderFilter] = useState([]);

    /* ---------- fetch all servers + users ---------- */
    const fetchServers = useCallback(async () => {
        setIsLoading(true);
        setLoadError("");
        try {
            const employee_id = getEmployeeId();
            console.log("Fetching servers with employee_id:", employee_id); // Debug log

            const res = await axios.get(`${SERVER_URL}/Cloud/serverHistory.php`, {
                params: { employee_id: employee_id },
            });
            if (res.data.success) {
                setRawRows(res.data.data || []);
            } else {
                setLoadError(res.data.message || "Failed to load servers.");
                setRawRows([]);
            }
        } catch (err) {
            console.error("Error fetching servers:", err);
            setLoadError("Could not reach the server. Please check your connection and try again.");
            setRawRows([]);
        } finally {
            setIsLoading(false);
        }
    }, [getEmployeeId]);

    const fetchUsersByServerPort = async (serverPort) => {
        try {
            const employee_id = getEmployeeId();
            const res = await axios.get(`${SERVER_URL}/Cloud/serverUserHistory.php`, {
                params: {
                    server_port: serverPort,
                    employee_id: employee_id
                }
            });
            if (res.data.success) setUserData(res.data.data || []);
            else {
                notify(res.data.message || "Failed to load users.", "error");
                setUserData([]);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
            notify("Could not load users for this server.", "error");
            setUserData([]);
        }
    };

    useEffect(() => {
        fetchServers();
        setShowRenewalModal(true);
    }, [fetchServers]);

    /* ---------- group joined rows into one entry per server ---------- */
    const servers = useMemo(() => {
        const byId = new Map();

        rawRows.forEach((row) => {
            const id = row.server_id;
            if (!byId.has(id)) {
                byId.set(id, {
                    server_id: id,
                    server_port: row.server_port,
                    server_name: row.server_name,
                    provider_name: row.provider_name,
                    server_user_id: row.server_user_id,
                    server_password: row.server_password,
                    ram: row.ram,
                    rom: row.rom,
                    cost_price: row.cost_price,
                    created_at: row.server_created_at,
                    updated_at: row.server_updated_at,
                    is_active: row.server_active,
                    number_of_users: 0,
                    active_users: 0,
                    inactive_users: 0,
                    sellByCurrency: {},
                });
            }

            const entry = byId.get(id);
            if (row.user_slNo !== null && row.user_slNo !== undefined) {
                entry.number_of_users += 1;
                if (normalizeStatus(row.status) === "active") entry.active_users += 1;
                else entry.inactive_users += 1;
                const currency = row.currency || "INR";
                entry.sellByCurrency[currency] = (entry.sellByCurrency[currency] || 0) + (Number(row.sell_price) || 0);
            }
        });

        return Array.from(byId.values()).map((s) => {
            let profitPercent = null;
            if (isAdmin && s.cost_price > 0 && Object.keys(s.sellByCurrency).length === 1 && s.sellByCurrency.INR !== undefined) {
                profitPercent = Number((((s.sellByCurrency.INR - s.cost_price) / s.cost_price) * 100).toFixed(1));
            }
            return { ...s, profitPercent };
        });
    }, [rawRows, isAdmin]);

    /* ---------- flat user list (all servers) for charts / groups ---------- */
    const flatUsers = useMemo(() => {
        return rawRows
            .filter((row) => row.user_slNo !== null && row.user_slNo !== undefined)
            .map((row) => ({
                ...row,
                server_name: row.server_name,
                provider_name: row.provider_name,
            }));
    }, [rawRows]);

    /* ---------- filter options ---------- */
    const serverNameOptions = useMemo(() => [...new Set(servers.map((r) => r.server_name))].filter(Boolean).map((name) => ({ label: name, value: name })), [servers]);
    const serverPortOptions = useMemo(() => [...new Set(servers.map((r) => String(r.server_port)))].filter(Boolean).map((port) => ({ label: port, value: port })), [servers]);
    const providerOptionsList = useMemo(() => [...new Set(servers.map((r) => r.provider_name))].filter(Boolean).map((p) => ({ label: p, value: p })), [servers]);

    // Server IDs whose *users* match the search term (company name, contact
    // person, contact number, username). Lets a search for a customer surface
    // only the servers that customer actually sits on.
    const serverIdsMatchingUserSearch = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return null;
        const ids = new Set();
        flatUsers.forEach((u) => {
            const hit =
                String(u.company_name || "").toLowerCase().includes(q) ||
                String(u.contact_person_name || "").toLowerCase().includes(q) ||
                String(u.contact_person_number || "").toLowerCase().includes(q) ||
                String(u.user_name || "").toLowerCase().includes(q);
            if (hit) ids.add(u.server_id);
        });
        return ids;
    }, [flatUsers, search]);

    const filteredServerData = useMemo(() => {
        const q = search.toLowerCase();
        return servers.filter((row) => {
            const matchesSearch =
                !q ||
                String(row.server_name || "").toLowerCase().includes(q) ||
                String(row.server_port || "").toLowerCase().includes(q) ||
                String(row.provider_name || "").toLowerCase().includes(q) ||
                (serverIdsMatchingUserSearch && serverIdsMatchingUserSearch.has(row.server_id));
            const matchesServerName = serverNameFilter.length === 0 || serverNameFilter.includes(row.server_name);
            const matchesServerPort = serverPortFilter.length === 0 || serverPortFilter.includes(String(row.server_port));
            const matchesProvider = providerFilter.length === 0 || providerFilter.includes(row.provider_name);
            return matchesSearch && matchesServerName && matchesServerPort && matchesProvider;
        });
    }, [servers, search, serverNameFilter, serverPortFilter, providerFilter, serverIdsMatchingUserSearch]);

    const overallStats = useMemo(() => {
        const totalServers = filteredServerData.length;
        const totalUsers = filteredServerData.reduce((sum, s) => sum + s.number_of_users, 0);
        const activeUsers = filteredServerData.reduce((sum, s) => sum + s.active_users, 0);
        const inactiveUsers = filteredServerData.reduce((sum, s) => sum + s.inactive_users, 0);
        return { totalServers, totalUsers, activeUsers, inactiveUsers };
    }, [filteredServerData]);

    const excelColumns = [
        { header: "Server Port", key: "server_port", type: "text" },
        { header: "Server Name", key: "server_name", type: "text" },
        { header: "Provider Name", key: "provider_name", type: "text" },
        { header: "RAM", key: "ram", type: "text" },
        { header: "ROM", key: "rom", type: "text" },
        ...(isAdmin ? [{ header: "Cost Price (INR)", key: "cost_price", type: "text" }] : []),
        { header: "Total Users", key: "number_of_users", type: "text" },
        { header: "Active Users", key: "active_users", type: "text" },
        { header: "Inactive Users", key: "inactive_users", type: "text" },
    ];

    /* ---------- server: add / edit ---------- */
    const openAddServer = () => {
        setEditingServer(null);
        setServerForm(EMPTY_SERVER_FORM);
        setFormError("");
        setShowServerModal(true);
    };

    const handleEditServer = (server) => {
        setEditingServer(server);
        setServerForm({
            server_port: server.server_port ?? "",
            server_name: server.server_name ?? "",
            provider_name: server.provider_name ?? "",
            server_user_id: server.server_user_id ?? "",
            server_password: "",
            ram: server.ram ?? "",
            rom: server.rom ?? "",
            cost_price: server.cost_price ?? "",
        });
        setFormError("");
        setShowServerModal(true);
    };

    const closeServerModal = () => {
        setShowServerModal(false);
        setEditingServer(null);
        setFormError("");
    };

    const handleServerSubmit = async () => {
        if (!serverForm.server_port || !serverForm.server_name.trim()) {
            setFormError("Server name and server port are required.");
            return;
        }
        setFormError("");
        setSavingServer(true);
        try {
            const endpoint = editingServer
                ? `${SERVER_URL}/Cloud/updateServerDetails.php`
                : `${SERVER_URL}/Cloud/createServer.php`;

            const payload = {
                ...serverForm,
                action_taken_by: getEmployeeName() || getActionTakenBy(),
                ...(editingServer ? { slNo: editingServer.server_id } : {}),
            };

            const res = await postForm(endpoint, payload);

            if (res.data.success) {
                notify(editingServer ? "Server updated successfully." : "Server added successfully.");
                closeServerModal();
                fetchServers();
            } else {
                setFormError(res.data.message || "Something went wrong.");
            }
        } catch (err) {
            console.error("Error saving server:", err);
            setFormError("Could not save the server. Please try again.");
        } finally {
            setSavingServer(false);
        }
    };

    /* ---------- users: view ---------- */
    const handleViewUsers = async (server) => {
        setSelectedServer(server);
        setShowUserDetailsModal(true);
        await fetchUsersByServerPort(server.server_port);
    };

    /* ---------- users: add / edit ---------- */
    const handleAddUser = (server) => {
        setSelectedServer(server);
        setEditingUser(null);
        // user_id is generated server-side on save, so it is no longer set here.
        setUserForm({
            ...EMPTY_USER_FORM,
            server_port: server.server_port,
        });
        setFormError("");
        setShowUserDetailsModal(false);
        setShowUserModal(true);
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setUserForm({
            user_id: user.user_id ?? "",
            company_name: user.company_name ?? "",
            contact_person_name: user.contact_person_name ?? "",
            contact_person_number: user.contact_person_number ?? "",
            user_name: user.user_name ?? "",
            user_password: "",
            licence_type: user.licence_type ?? "",
            server_port: user.server_port ?? selectedServer?.server_port ?? "",
            actual_user_name: user.actual_user_name ?? "",
            actual_user_password: "",
            activation_date: user.activation_date ?? "",
            expire_date: user.expire_date ?? "",
            next_renewal_date: user.next_renewal_date ?? "",
            prorata_expire_date: user.prorata_expire_date ?? "",
            rate: user.rate ?? "",
            discount: user.discount ?? "",
            currency: user.currency ?? "",
            gateway_path: user.gateway_path ?? "",
            referred_person: user.referred_person ?? "",
            referred_person_number: user.referred_person_number ?? "",
            sell_person_id: user.sell_person_id ?? "",
            sell_person_name: user.sell_person_name ?? "",
            region: user.region ?? "",
            remarks: user.remarks ?? "",
            status: user.status || "Active",
            reason: user.reason ?? "",
            subscription: user.subscription ? String(user.subscription).match(/\d+/)?.[0] ?? "1" : "1",
        });
        setFormError("");
        setShowUserDetailsModal(false);
        setShowUserModal(true);
    };

    const closeUserModal = () => {
        setShowUserModal(false);
        setEditingUser(null);
        setFormError("");
        if (selectedServer) setShowUserDetailsModal(true);
    };

    const handleUserSubmit = async () => {
        if (!userForm.user_name.trim() || !userForm.user_password.trim() || !userForm.company_name.trim() || !userForm.server_port) {
            setFormError("Username, Password, Company name and Server are required.");
            return;
        }
        setFormError("");
        setSavingUser(true);
        try {
            const actionTakenBy = getEmployeeName() || getActionTakenBy();
            let res;

            if (editingUser) {
                const payload = { action: "update", slNo: editingUser.slNo, action_taken_by: actionTakenBy, ...userForm };
                if (!userForm.user_password) delete payload.user_password;
                if (!userForm.actual_user_password) delete payload.actual_user_password;
                res = await postForm(`${SERVER_URL}/Cloud/serverUserUpdate.php`, payload);
            } else {
                res = await postForm(`${SERVER_URL}/Cloud/createUser.php`, { ...userForm, action_taken_by: actionTakenBy });
            }

            if (res.data.success) {
                notify(editingUser ? "User updated successfully." : "User added successfully.");
                closeUserModal();
                if (selectedServer) fetchUsersByServerPort(selectedServer.server_port);
                fetchServers();
            } else {
                setFormError(res.data.message || "Something went wrong.");
            }
        } catch (err) {
            console.error("Error saving user:", err);
            setFormError("Could not save the user. Please try again.");
        } finally {
            setSavingUser(false);
        }
    };

    /* ---------- users: activate / deactivate ---------- */
    const askToggleUserStatus = (user) => {
        const nextStatus = normalizeStatus(user.status) === "active" ? "deactivate" : "activate";
        setReasonDialog({ user, nextStatus });
    };

    const confirmToggleUserStatus = async (reason) => {
        const { user, nextStatus } = reasonDialog;
        setReasonDialog(null);
        try {
            const res = await postForm(`${SERVER_URL}/Cloud/serverUserUpdate.php`, {
                action: nextStatus,
                slNo: user.slNo,
                reason,
                action_taken_by: getEmployeeName() || getActionTakenBy(),
            });
            if (res.data.success) {
                notify(`User ${nextStatus}d successfully.`);
                if (selectedServer) fetchUsersByServerPort(selectedServer.server_port);
                fetchServers();
            } else {
                notify(res.data.message || "Failed to update user status.", "error");
            }
        } catch (err) {
            console.error("Error updating user status:", err);
            notify("Could not update user status. Please try again.", "error");
        }
    };

    /* ---------- details panels ---------- */
    const openServerDetails = (server) => {
        setDetailsSubject({ kind: "server", data: server });
        setShowDetailsModal(true);
    };
    const openUserDetails = (user) => {
        setDetailsSubject({ kind: "user", data: user });
        setShowDetailsModal(true);
    };

    return (
        <>
            <div className="min-h-screen w-full font-[Inter]" style={{ background: "#f0f4f8" }}>
                <div className="fixed top-0 left-0 right-0 h-0.5 z-50" style={{ background: "linear-gradient(90deg, #1e40af 0%, #0369a1 50%, #0e7490 100%)" }} />

                <Sidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} onLogout={() => { localStorage.clear(); }} />

                <div className="sticky top-0 z-20 border-b border-slate-200" style={{ background: "#ffffff", boxShadow: "0 1px 4px rgba(15,23,42,0.08)" }}>
                    <div className="px-7 py-3.5 flex items-center justify-between">
                        <div className="flex items-center gap-4">
                            <button onClick={() => setSidebarOpen(!sidebarOpen)} className="p-2 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-600 transition-colors border-0">
                                <Bars3Icon className="h-5 w-5" />
                            </button>
                            <div className="flex items-center gap-2.5">
                                <div className="w-1 h-6 rounded-full" style={{ background: "linear-gradient(180deg, #1e40af, #0369a1)" }} />
                                <h1 className="text-lg font-bold m-0 tracking-tight text-slate-800">Server Management</h1>
                                
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <button onClick={() => setShowRenewalModal(true)} className="h-10 px-4 rounded-lg text-sm font-semibold text-blue-700 border border-blue-200 bg-blue-50 hover:bg-blue-100">
                                Renewal Report
                            </button>
                            {isAdmin && (
                                <button onClick={() => setShowLogsModal(true)} className="h-10 px-4 rounded-lg text-sm font-semibold text-indigo-700 border border-indigo-200 bg-indigo-50 hover:bg-indigo-100">
                                    View Logs
                                </button>
                            )}
                            <button onClick={openAddServer} className="flex items-center gap-1.5 h-10 px-4 rounded-lg text-sm font-semibold text-white transition-all" style={{ background: "linear-gradient(90deg, #1e40af, #0369a1)", boxShadow: "0 1px 4px rgba(15,23,42,0.15)" }}>
                                <PlusIcon className="w-4 h-4" />
                                Add Server
                            </button>
                            {/* <ExcelExport data={filteredServerData} columns={excelColumns} fileName="Server_Report.xlsx" reportTitle="Server Report" /> */}
                        </div>
                    </div>
                </div>

                <div className="relative z-10 flex flex-col gap-4 p-5">
                    {loadError && (
                        <div className="rounded-lg px-4 py-3 bg-rose-50 border border-rose-200 text-rose-700 text-sm flex items-center justify-between">
                            <span>{loadError}</span>
                            <button onClick={fetchServers} className="font-semibold underline">Retry</button>
                        </div>
                    )}

                    <div className="rounded-xl px-5 py-4 bg-white border border-slate-200" style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="flex items-center justify-between gap-3 flex-wrap w-full">
                            <div className="flex flex-wrap gap-2 flex-shrink-0">
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-blue-50 text-blue-700 border border-blue-200">
                                    <span className="w-2 h-2 rounded-full bg-blue-500" /> Total Servers <span className="font-extrabold">{overallStats.totalServers}</span>
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-indigo-50 text-indigo-700 border border-indigo-200">
                                    <span className="w-2 h-2 rounded-full bg-indigo-500" /> Total Users <span className="font-extrabold">{overallStats.totalUsers}</span>
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-emerald-50 text-emerald-700 border border-emerald-200">
                                    <span className="w-2 h-2 rounded-full bg-emerald-500" /> Active Users <span className="font-extrabold">{overallStats.activeUsers}</span>
                                </div>
                                <div className="flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider bg-rose-50 text-rose-700 border border-rose-200">
                                    <span className="w-2 h-2 rounded-full bg-rose-500" /> Inactive Users <span className="font-extrabold">{overallStats.inactiveUsers}</span>
                                </div>
                            </div>
                            <div className="flex items-center gap-3 ml-auto">
                                <input type="text" placeholder="Search server, port, company, customer..." value={search} onChange={(e) => setSearch(e.target.value)} className="w-64 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 h-10 text-sm text-slate-700" />
                            </div>
                        </div>
                    </div>

                    {/* ── CHARTS (admin only) ── */}
                    {isAdmin && (
                        <DashboardCharts
                            servers={servers}
                            flatUsers={flatUsers}
                            onShowGroup={(title, users) => setGroupModal({ title, users })}
                        />
                    )}

                    {/* ── SERVER TABLE ── */}
                    <div className="rounded-xl overflow-hidden bg-white border border-slate-200" style={{ boxShadow: "0 1px 6px rgba(15,23,42,0.06)" }}>
                        <div className="overflow-x-auto overflow-y-auto max-h-[560px] att-scrollbar">
                            <table className="min-w-[1600px] w-full text-sm">
                                <thead className="sticky top-0 z-10" style={{ background: "#f8fafc" }}>
                                    <tr>
                                        <th className="p-3 text-left border-b border-slate-200 min-w-[100px]">
                                            <TableHeaderFilter label="Server Port" options={serverPortOptions} selectedValues={serverPortFilter} onChange={setServerPortFilter} fullWidth />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200 min-w-[150px]">
                                            <TableHeaderFilter label="Server Name" options={serverNameOptions} selectedValues={serverNameFilter} onChange={setServerNameFilter} fullWidth />
                                        </th>
                                        <th className="p-3 text-left border-b border-slate-200 min-w-[170px]">
                                            <TableHeaderFilter label="Provider" options={providerOptionsList} selectedValues={providerFilter} onChange={setProviderFilter} fullWidth />
                                        </th>
                                        <th className="p-3 text-center border-b border-slate-200 min-w-[150px]"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Capacity</span></th>
                                        <th className="p-3 text-center border-b border-slate-200 min-w-[110px]"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Server User ID</span></th>
                                        <th className="p-3 text-center border-b border-slate-200 min-w-[100px]"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Password</span></th>
                                        {isAdmin && <th className="p-3 text-center border-b border-slate-200 min-w-[100px]"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Cost Price</span></th>}
                                        {isAdmin && <th className="p-3 text-center border-b border-slate-200 min-w-[110px]"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Sell Price</span></th>}
                                        {isAdmin && <th className="p-3 text-center border-b border-slate-200 min-w-[90px]"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Profit %</span></th>}
                                        <th className="p-3 text-center border-b border-slate-200 min-w-[90px]"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Users</span></th>
                                        <th className="p-3 text-center border-b border-slate-200 min-w-[190px]"><span className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Actions</span></th>
                                    </tr>
                                </thead>

                                <tbody>
                                    {isLoading ? (
                                        <tr><td colSpan={isAdmin ? 11 : 8} className="p-8 text-center text-slate-400 text-sm"><span className="inline-block animate-pulse">Loading…</span></td></tr>
                                    ) : filteredServerData.length === 0 ? (
                                        <tr><td colSpan={isAdmin ? 11 : 8} className="p-8 text-center text-slate-400 text-sm">{loadError ? "Unable to load servers." : "No servers found."}</td></tr>
                                    ) : (
                                        filteredServerData.map((row) => (
                                            <tr key={row.server_id} className="border-t border-slate-100 transition-colors hover:bg-blue-50/60">
                                                <td className="p-3 text-slate-500 text-xs font-medium">{row.server_port}</td>
                                                <td className="p-3 text-slate-800 text-xs font-medium">{row.server_name}</td>
                                                <td className="p-3 text-slate-500 text-xs">{row.provider_name || "—"}</td>
                                                <td className="p-3 text-center text-slate-500 text-xs">{`RAM- ${row.ram || "—"} / ROM- ${row.rom || "—"}`}</td>
                                                <td className="p-3 text-center text-slate-500 text-xs">{row.server_user_id}</td>
                                                <td className="p-3 text-center text-slate-500 text-xs font-mono">{row.server_password}</td>
                                                {isAdmin && <td className="p-3 text-center text-slate-500 text-xs">{formatMoney(row.cost_price)}</td>}
                                                {isAdmin && (
                                                    <td className="p-3 text-center text-slate-500 text-xs">
                                                        {Object.keys(row.sellByCurrency).length === 0 ? "—" : Object.entries(row.sellByCurrency).map(([cur, amt]) => <div key={cur}>{formatMoney(amt, cur)}</div>)}
                                                    </td>
                                                )}
                                                {isAdmin && <td className="p-3 text-center text-xs font-semibold">{row.profitPercent != null ? <span className={row.profitPercent >= 0 ? "text-emerald-600" : "text-rose-600"}>{row.profitPercent}%</span> : "—"}</td>}
                                                <td className="p-3 text-center text-slate-500 text-xs">{row.active_users}/{row.number_of_users}</td>
                                                <td className="p-3 text-center">
                                                    <div className="flex gap-2 justify-center flex-wrap">
                                                        <button onClick={() => handleViewUsers(row)} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-blue-700 border border-blue-200 hover:bg-blue-100 bg-blue-50 transition-colors">
                                                            <EyeIcon className="w-3.5 h-3.5 inline mr-1" />Users
                                                        </button>
                                                        <button onClick={() => handleAddUser(row)} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-emerald-700 border border-emerald-200 hover:bg-emerald-100 bg-emerald-50 transition-colors">
                                                            <FaUserPlus className="w-3.5 h-3.5 inline mr-1" />Add User
                                                        </button>
                                                        <button onClick={() => handleEditServer(row)} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-amber-700 border border-amber-200 hover:bg-amber-100 bg-amber-50 transition-colors">
                                                            <PencilIcon className="w-3.5 h-3.5 inline" />
                                                        </button>
                                                        <button onClick={() => openServerDetails(row)} className="px-2.5 py-1 rounded-lg text-xs font-semibold text-indigo-700 border border-indigo-200 hover:bg-indigo-100 bg-indigo-50 transition-colors">
                                                            <InformationCircleIcon className="w-3.5 h-3.5 inline" />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                </div>
            </div>

            <ServerFormModal
                open={showServerModal}
                editingServer={editingServer}
                form={serverForm}
                setForm={setServerForm}
                formError={formError}
                saving={savingServer}
                onCancel={closeServerModal}
                onSubmit={handleServerSubmit}
            />

            <UserFormModal
                open={showUserModal}
                editingUser={editingUser}
                selectedServer={selectedServer}
                form={userForm}
                setForm={setUserForm}
                formError={formError}
                saving={savingUser}
                onCancel={closeUserModal}
                onSubmit={handleUserSubmit}
            />

            <UsersListModal
                open={showUserDetailsModal}
                server={selectedServer}
                users={userData}
                onClose={() => { setShowUserDetailsModal(false); setSelectedServer(null); setUserData([]); }}
                onAddUser={handleAddUser}
                onEditUser={handleEditUser}
                onToggleStatus={askToggleUserStatus}
                onViewDetails={openUserDetails}
                onViewHistory={(user) => setHistorySubject(user)}
            />

            <DetailsModal open={showDetailsModal} subject={detailsSubject} isAdmin={isAdmin} onClose={() => setShowDetailsModal(false)} />

            <UserHistoryModal open={!!historySubject} user={historySubject} onClose={() => setHistorySubject(null)} />

            <RenewalReportModal open={showRenewalModal} onClose={() => setShowRenewalModal(false)} notify={notify} />

            {isAdmin && (
                <AdminLogsModal open={showLogsModal} onClose={() => setShowLogsModal(false)} notify={notify} />
            )}

            <UserGroupModal
                open={!!groupModal}
                title={groupModal?.title}
                users={groupModal?.users || []}
                onClose={() => setGroupModal(null)}
            />

            <ReasonDialog
                open={!!reasonDialog}
                title={reasonDialog ? `Reason to ${reasonDialog.nextStatus} ${reasonDialog.user.company_name}` : ""}
                confirmLabel={reasonDialog ? (reasonDialog.nextStatus === "activate" ? "Activate" : "Deactivate") : ""}
                onCancel={() => setReasonDialog(null)}
                onConfirm={confirmToggleUserStatus}
            />

            <Toast toast={toast} onDismiss={() => setToast(null)} />
        </>
    );
};

export default ServerManagement;